import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import type {BaseSyntheticEvent, FC, ReactElement} from 'react'
import {useCallback, useEffect, useRef, useState} from 'react'
import BasicInput from './BasicInput.tsx'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'

type SimpleSearchProps = {
  onSearch: (searchTerm: string) => void
  label?: string
  caseSensitive?: boolean
  size?: 'small' | 'medium' | 'large'
  dataCy?: string
  isLoading?: boolean
  // Pre-fills the input on mount (e.g. a search term persisted in the URL).
  // onSearch is NOT fired for it — callers already applied that value.
  initialValue?: string
}

const SimpleSearch: FC<SimpleSearchProps> = ({
  onSearch,
  label,
  caseSensitive = false,
  size = 'medium',
  dataCy,
  isLoading = false,
  initialValue = '',
}): ReactElement => {
  const [t] = useCustomTranslation('common')
  const [searchTerm, setSearchTerm] = useState<string>(initialValue)
  const [isSearching, setIsSearching] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  // Track previous searchTerm to skip firing onSearch when nothing changed.
  // This prevents spurious fires on mount/remount (including StrictMode double-invoke)
  // and when only the onSearch callback reference changes.
  const prevSearchTermRef = useRef(searchTerm)
  // Keep the latest onSearch in a ref so the debounce effect below depends only on the
  // search term — NOT on the onSearch callback identity. Several callers recreate
  // onSearch on every render (e.g. BasicPopoverWithSearch.handleSetSearchItem feeding
  // the Digital Twin filters). If onSearch were an effect dependency, each parent
  // re-render would re-run the effect; the cleanup would clear the pending 300ms timer,
  // and the prevSearchTermRef guard would then early-return without rescheduling it — so
  // the typed term never reached onSearch (no fetch, list never filtered). See ADO Bug
  // #5387 / Intercom "Suche im Zwilling defekt".
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  const handleSearchItems = useCallback((event: BaseSyntheticEvent) => {
    event.stopPropagation()
    const {
      target: {value: newSearchTerm},
    } = event
    setSearchTerm(newSearchTerm)

    if (newSearchTerm.length > 0) {
      setIsSearching(true)
    }
  }, [])

  useEffect(() => {
    if (prevSearchTermRef.current === searchTerm) return
    prevSearchTermRef.current = searchTerm

    searchTimeoutRef.current = setTimeout(() => {
      if (caseSensitive) {
        onSearchRef.current(searchTerm)
      } else {
        onSearchRef.current(searchTerm.toLowerCase())
      }

      setTimeout(() => {
        setIsSearching(false)
      }, 300)
    }, 300)

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [searchTerm, caseSensitive])

  useEffect(() => {
    setIsSearching(isLoading)
  }, [isLoading])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <BasicInput
      inputRef={inputRef}
      data-testid="mbai-simple-search-component"
      id="mbai-main-search-component"
      data-cy={dataCy}
      InputProps={{
        startAdornment: (
          <SearchTwoToneIcon
            sx={{
              fontSize: '1.25rem',
              color: 'text.disabled',
            }}
          />
        ),
        endAdornment: (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '24px',
              height: '24px',
              justifyContent: 'center',
              mr: '10px',
            }}
          >
            {isSearching && (
              <CircularProgress
                size={16}
                sx={{
                  color: 'primary.main',
                }}
              />
            )}
          </Box>
        ),
      }}
      onChange={handleSearchItems}
      defaultValue={initialValue}
      label={label || t('search')}
      displayLabel={false}
      mbaiSize={size}
      onKeyDown={(event: BaseSyntheticEvent) => {
        event.stopPropagation()
      }}
    />
  )
}

export default SimpleSearch
